import { Box, Fade, Tooltip, Typography } from '@mui/material'
import i18n from 'lib/i18'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import { useEffect, useRef, useState } from 'react'
import { PickingStatus, Product, ProductInfo } from 'types/model/product'
import {
  ProductColWrapper,
  ProductName,
  TooltipStyling,
} from './OrderProduct.styled'
import { ProductColPills } from './ProductColPills'

export const ProductColumn = ({
  productInfo,
  product,
}: {
  productInfo: ProductInfo[]
  product: Product
}) => {
  const productNamesRef = useRef<HTMLParagraphElement | null>(null)
  // hover true if there is an ellipsis
  const [hoverStatus, setHoverStatus] = useState(false)
  const details = productInfo.filter(
    (p) => p.extId === product.sku || p.gtin === product.gtin.toString()
  )[0]
  const substitutedProductName = productInfo.filter(
    (p) =>
      p.extId === (product.collectedProduct && product.collectedProduct.sku) ||
      p.gtin ===
        (product.collectedProduct && product.collectedProduct.gtin.toString())
  )[0]?.name
  const substitutedProductGtin = productInfo.filter(
    (p) =>
      p.extId === (product.collectedProduct && product.collectedProduct.sku) ||
      p.gtin ===
        (product.collectedProduct && product.collectedProduct.gtin.toString())
  )[0]?.gtin
  const substituted =
    product.pickingStatus.toLowerCase() === PickingStatus.SUBSTITUTED

  const offer = product.offers?.length > 0 ? product.offers[0].name : null
  const offerName =
    offer?.[i18n.language] ?? offer?.[contentLanguages[0]] ?? null

  // product pill info
  const required = product.fulfilmentFlags?.includes('REQUIRED')
  const { extraProduct } = product

  // checks whether the text overflows and there is an ellipsis
  const checkTextOverflow = () => {
    let compare = false
    if (productNamesRef !== null && productNamesRef.current !== null) {
      compare = Boolean(
        productNamesRef.current &&
          productNamesRef.current.scrollWidth >
            productNamesRef.current.clientWidth
      )
    }
    setHoverStatus(compare)
  }

  useEffect(() => {
    checkTextOverflow()
    window.addEventListener('resize', checkTextOverflow)
    return () => {
      window.removeEventListener('resize', checkTextOverflow)
    }
  }, [])

  return details && (details.name || details.details.brandName) ? (
    <Box>
      {substituted && (
        <ProductColWrapper>
          <Box
            sx={{
              maxWidth: '200px',
              height: '100%',
              whiteSpace: 'nowrap',
            }}>
            <Box display='flex' alignItems='center'>
              <Tooltip
                TransitionComponent={Fade}
                title={
                  <Typography
                    variant='body1'
                    sx={{
                      margin: theme.spacing(0.5),
                      color: theme.palette.text.primary,
                    }}>
                    {substitutedProductName}
                  </Typography>
                }
                componentsProps={{
                  tooltip: {
                    sx: TooltipStyling,
                  },
                }}>
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': { cursor: 'pointer' },
                  }}>
                  {substitutedProductName}
                </Typography>
              </Tooltip>
              <ProductColPills
                extraProduct={extraProduct}
                required={required}
              />
            </Box>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {substitutedProductGtin || product.collectedProduct.gtin}
            </Typography>
          </Box>
        </ProductColWrapper>
      )}
      <ProductColWrapper>
        <Box
          sx={{
            maxWidth: '240px',
            height: '100%',
            whiteSpace: 'nowrap',
          }}>
          <Box display='flex' alignItems='center'>
            <Tooltip
              TransitionComponent={Fade}
              disableHoverListener={!hoverStatus}
              title={
                <Typography
                  variant='body1'
                  sx={{
                    margin: theme.spacing(0.5),
                    color: theme.palette.text.primary,
                  }}>
                  {details.name}
                </Typography>
              }
              componentsProps={{
                tooltip: {
                  sx: TooltipStyling,
                },
              }}>
              <ProductName
                variant='body1'
                ref={productNamesRef}
                hoverStatus={hoverStatus}
                substituted={substituted}>
                {details.name}
              </ProductName>
            </Tooltip>
            {!substituted && (
              <ProductColPills
                extraProduct={extraProduct}
                required={required}
              />
            )}
          </Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: substituted ? 'line-through' : 'none',
            }}>
            {details.gtin || product.gtin}
          </Typography>
          {offerName && (
            <Typography sx={{ color: theme.palette.primary.main }}>
              {offerName.toUpperCase()}
            </Typography>
          )}
        </Box>
      </ProductColWrapper>
    </Box>
  ) : (
    <Box />
  )
}
